import { getDataReaderClient } from "@multiweb/clients/util";
import { IRowData } from "@multiweb/types/contracts";
import { getClassItemsRights } from "Global/Base/Rights/utils";
import { ConditionParams, IDataSource, IDataSourceLoadInfo, IDataSourceLoadInfoBase, IEditableDataSource, IOrderBy, IPermissionData, IRecordsInfo, ISaveRowEventArgs } from "../../../Global/Base/DataSources/Interfaces";
import { AwaitableStandardEvent, Event } from "../../../Global/Base/Events";
import { NotImplementedException } from "../../../Global/Base/Exceptions";
import { CancellationToken, CancellationTokenSource } from "../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../Global/Base/Promises/PromiseUtils";
import { IOWObject } from "../../../OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList, OWObjectListItem } from "../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { OWManager } from "../../../OW.Remoting/Remoting/OWManager";
import { IExtendedObjectOperation } from "../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { RequestContext } from "../../../OW.Remoting/Remoting/RequestContext";
import { ClassItemType } from "../../../OW.Remoting/Remoting/Rights";
import { FrameContext } from "../../UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "../../UI/Utilities/SharedUIMethods";
import { DBDataSource, IDataSourceParams } from "./DBDataSource";
import { IActualObjectChangeEventArgs, INewRow, SaveResultData } from "./EditableDBDataSource";
import { IObjectDataSource } from "./IObjectDataSource";

/**
 * Trida reprezentujici zdroj dat pro konzumenta dat
 */
export class NPDataSource implements IEditableDataSource, IObjectDataSource {
    //#region Events
    /**
     * Vraci startindex pro cteni dat
     */
    public readonly valueChanged: Event<NPDataSource, any> = new Event<NPDataSource, any>();

    public readonly setValueChanged: Event<NPDataSource, Promise<void>> = new Event<NPDataSource, Promise<void>>();

    /**
     * Pokud jsou dočtena data
     */
    public readonly dataLoaded: Event<IDataSource, IDataSourceLoadInfo> = new Event<IDataSource, IDataSourceLoadInfo>();

    /**
     * Pokud je zrušeno načítání dat
     */
    public readonly loadingCancelled: Event<IDataSource, any> = new Event<IDataSource, IDataSourceLoadInfoBase>();

    /**
     * Pokud je spuštěno načítání dat
     */
    public readonly loadingStarted: Event<IDataSource, any> = new Event<IDataSource, any>();

    /**
    * Pokud je ukončeno načítání dat. Vyskytuje se až po dataLoaded.
    */
    public readonly loadingEnded: Event<IDataSource, IDataSourceLoadInfoBase> = new Event<IDataSource, IDataSourceLoadInfoBase>();

    /**
     * Vraci informaci o zruseni zmen
     */
    public readonly changesCancelled: Event<IEditableDataSource, any> = new Event<IEditableDataSource, any>();

    /**
    * Vraci novy objekt T
    */
    public readonly newCreated: AwaitableStandardEvent<IEditableDataSource, Readonly<IRowData>> = new AwaitableStandardEvent<IEditableDataSource, Readonly<IRowData>>();

    /**
    * Vraci mazany radek
    * Parametr je key
    */
    public readonly deleted: Event<IEditableDataSource, any> = new Event<IEditableDataSource, any>();

    /** Vrací záznamy, u kterých právě začalo ukládání. Umožňuje před uložením provést dodatečné změny/kontroly */
    public readonly beforeSave: AwaitableStandardEvent<IEditableDataSource, Readonly<{ objects: ReadonlyArray<OWObject> }>> = new AwaitableStandardEvent<IEditableDataSource, Readonly<{ objects: ReadonlyArray<OWObject> }>>();

    /**
     * Vraci pri ulozeni novy key zaznamu
     */
    public readonly saved: Event<IEditableDataSource, Readonly<ISaveRowEventArgs>> = new Event<IEditableDataSource, Readonly<ISaveRowEventArgs>>();

    /**
     * Vraci ulozene zaznamy po zkonceni ukladani
     */
    public readonly afterSaved: Event<IEditableDataSource, Readonly<{ saved: ReadonlyArray<string> }>> = new Event<IEditableDataSource, Readonly<{ saved: ReadonlyArray<string> }>>();

    /**
    * Vraci radek a zmeneny atribut
    */
    public readonly attributesChanged: Event<IEditableDataSource, IActualObjectChangeEventArgs> = new Event<IEditableDataSource, IActualObjectChangeEventArgs>();

    /**
    * Vraci refreshnuty radek
    */
    public readonly rowRefreshed: Event<IEditableDataSource, Readonly<IRowData>>;

    /**
     * Informuje o zmene hodnoty atributu isSelectAll
     */
    public readonly isSelectAllChanged: Event<NPDataSource, any> = new Event<NPDataSource, any>();

    public readonly error: Event<DBDataSource, any> = new Event<DBDataSource, any>();

    public readonly permissionsChanged: Event<IEditableDataSource, Readonly<{ key: any; permissions: ReadonlyArray<IPermissionData> }>> = new Event<IEditableDataSource, Readonly<{ key: any; permissions: ReadonlyArray<IPermissionData> }>>();

    public get isSelectAll(): boolean {
        return this.objectList.isSelectAll;
    }

    public set isSelectAll(val: boolean) {
        if (this.objectList.isSelectAll === val)
            return;

        this.objectList.isSelectAll = val;

        this.isSelectAllChanged.trigger(this, val);
        this.valueChanged.trigger(this, this.objectList);
    }
    //#endregion Events

    //#region Fields
    protected _frameContext: FrameContext | undefined = undefined;
    private _permisions: Map<string, IPermissionData[]> = new Map<string, IPermissionData[]>();
    private _editedObjects: Set<string> = new Set<string>();
    private _internalRows: IRowData[];
    private _runningDataLoad: Promise<any>;
    private _runningDataLoadCTS: CancellationTokenSource;
    private _className: string;
    private _attributes: string[];
    private _addingItemsStack: Set<string> = new Set<string>();
    protected _fullLengthAttributes: string[] = [];

    /**
    * Property slouzci k definici trideni
    */
    public orderBy: IOrderBy[];
    /**
     * Nazev atributu pouziteho jako ID
     */
    public dataKey: string = "ID";
    public objectList: OWObjectList;
    /**
     * Property urcujici jestli se jedna o editovatelny DataSource
     */
    public isEditable: boolean;
    /**
     * Klic, ktery v IRow obsahuje opravneni pro jednotlive polozky
     */
    public permissionKey: string = "__permission_info";
    //#endregion Fields

    //#region Properties
    public get className(): string {
        return this._className;
    }

    public get editedObjectsCount(): number {
        return this._editedObjects.size;
    }

    /**
     * Vrací kompletní podmínku se všemi nutnými částmi, která byla použita pro načtení dat
     */
    public get condition(): string {
        return this.objectList.condition;
    }

    /**
    * Vrací podmínku potřebnou pro načtení dat
    */
    public getOriginalCondition(): string {
        throw new NotImplementedException();
    }

    /**
     * Vrací kompletní vstupy do podmínky, která byla použita pro načtení dat
     */
    public get conditionParams(): ConditionParams {
        return this.objectList.conditionParams;
    }
    //#endregion Properties

    public [Symbol.iterator](): Iterator<IRowData> {
        return this._internalRows[Symbol.iterator]();
    }

    //#region Constructors
    constructor(config: IDataSourceParams) {
        this._className = config.className;
        this.objectList = new OWObjectList(null, (cancellationToken) => this._getRequestContext(cancellationToken));
        this.objectList.condition = config.condition;
        this.objectList.conditionParams = config.conditionParams;
        this._frameContext = config.frameContext;
    }

    //#endregion Constructors
    public getRowPermission(dataKey: string): Promise<Array<IPermissionData>> {
        const permision = this._permisions.get(dataKey.toString());
        return Promise.resolve(permision);
    }

    /**
    * Vyvola dotaz na dalsi varku dat
    */
    public getNextData(startIndex?: number, count?: number, _keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        this._runningDataLoad = this._loadData(startIndex, count, cancellationToken);
        return this._runningDataLoad;
    }

    /**
     * TODO: tohle se musi sjednotit s EditableDBDataSource
     */
    private async _setRightsOnElement(actObject: OWObject): Promise<void> {
        const classItemsRightsList: Array<{ name: string; type: ClassItemType }> = [];
        for (let attrName of this._attributes) {
            if (attrName.indexOf(".") > -1)
                attrName = attrName.substr(0, attrName.indexOf("."));
            classItemsRightsList.push({ name: attrName, type: ClassItemType.Attribute });
        }
        // zavola metodu na sluzbu nacte prava a nastavi na atributy
        if (classItemsRightsList.length > 0) {
            if (actObject) {
                const perms = await getClassItemsRights(actObject, classItemsRightsList);

                //    let permision = this._permisions.get(actObject.instanceID);
                //  permision = []
                const permision = [];
                for (const p of perms.values()) {
                    const perm: IPermissionData = {
                        attrName: p.Name,
                        enabled: p.Enabled && actObject.canEdit,
                        required: p.Required,
                        visible: p.Visible,
                    };
                    permision.push(perm);
                }
                this._permisions.set(actObject.instanceID, permision);

                this.permissionsChanged.trigger(this, { key: actObject.instanceID, permissions: permision });
            }
        }
    }
    private async _actualizeExistingData(objList: OWObjectList | undefined): Promise<IDataSourceLoadInfo | undefined> {
        let dsLoadInfo: IDataSourceLoadInfo | undefined = undefined;
        if (objList) {
            // vytvořím si kopii, abych mohl pracovat s objekty, které přišly bezpečně.
            // Dochazí tu k mazani položek a pokud je na tu udalost navazano vic listeneru mohl by dojit s nespravnym poctem objektu
            const clonedObjList = objList.clone();
            //z aktualniho ObjectListu odstranim zaznamy, ktere nejsou v novych datech
            //a z nove prichozich dat presunu aktualni polozky do ObjectListu, který je v dataSourcu
            for (let i = 0; i < this.objectList.items.length; i++) {
                const currObj = this.objectList.items[i];
                const index = clonedObjList.items.findIndex(obj => obj.instanceID === currObj.instanceID);

                if (index === -1) {
                    this.objectList.items.splice(i, 1); // položka byla odstraněna
                    i--;
                } else {
                    clonedObjList.items.splice(index, 1); // položka stále existuje
                }
            }

            await this._actualizeInternalRows();
            dsLoadInfo = {
                startIndex: 0,
                dataGenerator: () => this._internalRows,
                lastBatch: true,
                isFiltered: false,
                refreshedData: false,
                emptyData: false,
            };
            this.dataLoaded.trigger(this, dsLoadInfo); //Odstraneni smazanych zaznamu

            for (let i = 0; i < clonedObjList.items.length; i++) {
                const checkObj = clonedObjList.items[i];
                if (!checkObj.instanceID) {
                    continue;
                } else {
                    if (!this._addingItemsStack.has(checkObj.instanceID)) //ulozeni objektů, které se budou gettovat. Aby se na ně klient neptal vícekrát
                        this._addingItemsStack.add(checkObj.instanceID);
                    else {
                        clonedObjList.items.splice(i, 1);
                        i--;
                    }
                }
            }
        }
        return dsLoadInfo;
    }

    private async _actualizeNewData(objList: OWObjectList | undefined): Promise<IDataSourceLoadInfo> {
        //přidání nových záznamů

        // TODO: bohužel toto nezachovává pořadí https://tfs/tfs/Jadro/Jadro%20.NET/_workitems?id=16711
        if (objList && objList.items.length > 0) {
            for (const newItem of objList.items) {
                const newObj = await newItem.getObject(this._getRequestContext());
                this._addingItemsStack.delete(newItem.instanceID);
                await this.addToObjectList(newObj);
                await this._setRightsOnElement(newObj);
            }
        }

        await this._actualizeInternalRows();
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: () => this._internalRows,
            lastBatch: true,
            isFiltered: false,
            refreshedData: false,
            emptyData: false,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
        return dsLoadInfo;
    }

    public async actualizeData(objList?: OWObjectList): Promise<void> {
        let dsLoadInfo: IDataSourceLoadInfo;

        try {
            this.loadingStarted.trigger(this, undefined);
            dsLoadInfo = await this._actualizeExistingData(objList);
            dsLoadInfo = await this._actualizeNewData(objList);
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo || { isFiltered: false, emptyData: true });
        }
    }

    /**
     * Priparava na aktualizaci zobrazenych dat, tak aby se nemusel refreshovat seznam
     */
    private async _actualizeInternalRows(): Promise<void> {
        this._internalRows = [];

        for (const item of this.objectList.items)
            this._internalRows.push(await this._createRowData(item));
    }

    private async _createRowData(objOrItem: IOWObject | OWObjectListItem): Promise<IRowData> {
        const data: IRowData = {};
        const actualObject = objOrItem instanceof IOWObject ? objOrItem : await objOrItem.getObject(this._getRequestContext());
        const attrNames = this._attributes;

        await PromiseUtils.for(0, attrNames.length, async (index) => {
            const attrName = attrNames[index];
            data[attrName] = await actualObject.getValue(attrName);
        });

        if (!data[this.dataKey])
            data[this.dataKey] = actualObject.instanceID;

        return data;
    }

    /**
     * Smaže dočtené záznamy v datasoursu
     * */
    public clearData(): void {
        this._internalRows = [];
        const dsLoadInfo: IDataSourceLoadInfo = {
            startIndex: 0,
            dataGenerator: () => this._internalRows,
            lastBatch: false,
            isFiltered: false,
            refreshedData: false,
            emptyData: true,
        };
        this.dataLoaded.trigger(this, dsLoadInfo);
    }

    private async _loadData(startIndexForEvent?: number, _countForEvent?: number, cancellationToken: CancellationToken = CancellationToken.none): Promise<void> {
        this.loadingStarted.trigger(this, startIndexForEvent);
        let dsLoadInfo: IDataSourceLoadInfo;

        try {
            if (this.objectList) {
                const objs = await this.objectList.getObjects(cancellationToken);
                this.objectList.clear();
                this._internalRows = [];

                for (let i = 0; i < objs.length; i++) {
                    const aktObj = objs[i];

                    const dr = await this.addToObjectList(aktObj);
                    await this._setRightsOnElement(aktObj);
                    this._internalRows.push(dr.row);
                }

                if (startIndexForEvent === undefined)
                    startIndexForEvent = 0;
                dsLoadInfo = {
                    startIndex: startIndexForEvent,
                    dataGenerator: () => this._internalRows,
                    lastBatch: true,
                    isFiltered: false,
                    refreshedData: false,
                    emptyData: false,
                };
                this.dataLoaded.trigger(this, dsLoadInfo);
            }
        } finally {
            this.loadingEnded.trigger(this, dsLoadInfo ?? { isFiltered: false, emptyData: true });
            this.changesCancelled.trigger(this, startIndexForEvent);
        }
    }

    public waitForReload(): Promise<void> {
        return this._runningDataLoad != null ? this._runningDataLoad : Promise.resolve();
    }

    /**
     * Vrací pole atributů, které datasource čte
     */
    public getAttrs(): string[] {
        return this._attributes;
    }

    /**
     * Vrací pole atributů, jejichž hodnoty se nemají ořežávat
    */
    public getFullLengthAttrs(): string[] {
        return this._fullLengthAttributes;
    }

    /**
    * Nastavi atributy, ktere chci docist
    * @param attrs seznam atributu
    * @param fullLengthAttributes Seznam atributu, jejichž hodnoty se nemají ořezávat
    */
    public setAttrs(attrs: string[], fullLengthAttributes?: string[]): void {
        this._attributes = attrs;
        this._fullLengthAttributes = fullLengthAttributes || [];
    }

    /**
     * Nastaví atributy, ktere chci dočíst a dočte data, pokud přibyl nějaký nový atribut
     * @param attrs seznam atributu
     */
    public async setAttrsAndLoad(attrs: string[], cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        const oldAttr = this._attributes;
        this._attributes = attrs;

        if (!attrs.every((v) => oldAttr.indexOf(v) > -1))
            return await this.loadNewData(false, cancellationToken);

        return null;
    }

    /**
    * Metoda aktualizuje datasource
    */
    public refreshData(_fetchCount?: number, keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        return this.loadNewData(keepSelectedRecords, cancellationToken);
    }

    /**
    * Vrati hodnoty zadanych atributu
    */
    public async getValues(key: any, attribute?: string[]): Promise<IRowData | null> {
        const obj = await this.getObject(key);
        if (!obj)
            return null;
        this._triggerObjectAttributesChanged(obj, obj.id);
        const attrNames = attribute || this._attributes;
        const irow: IRowData = {};

        await PromiseUtils.for(0, attrNames.length, async (index) => {
            const attrName = attrNames[index];
            irow[attrName] = await obj.getValue(attrName);
        });

        irow[this.dataKey] = key;
        irow[this.permissionKey] = await this.getRowPermission(obj.instanceID);

        return irow;
    }

    /**
    * Nastavi atributy, ktere chci docist
    * @param attrs seznam atributu
    */
    public setValues(key: any, values: IRowData): Promise<void> {
        const okeys = Object.keys(values);
        const fnc = PromiseUtils.for(0, okeys.length, async (index) => {
            const attrName = okeys[index];
            const value = values[attrName];
            const obj = await this.getObject(key);
            obj.isChanged = true;
            await obj.setValue(attrName, value);
            await this._setRightsOnElement(obj);
        });

        this.setValueChanged.trigger(this, fnc);

        return fnc;
    }

    public loadNewData(_keepSelectedRecords?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<any> {
        this._cancelLoading();

        this._runningDataLoad = this._loadData(undefined, undefined, cancellationToken);

        return this._runningDataLoad;
    }

    protected _cancelLoading(): void {
        // pokud se spustí nové dočítání dat a nějaké dočítání ještě běží, tak se to staré zruší a zahodí
        if (this._runningDataLoad && this._runningDataLoadCTS) {
            this._runningDataLoadCTS.cancel();

            this.loadingCancelled.trigger(this, null);
        }
    }

    public getObject(instanceId: string): Promise<OWObject> {
        return this.objectList.getItemByInstanceID(instanceId);
    }

    /**
     * Vytvoří nový řádek a oznámí jeho vytvoření
     * @param attrName Pole stringu obsahujici seznam atributu
     * @param actualObject Actual object pro nacteni hodnot
     */
    public async newRow(extendedOperations?: IExtendedObjectOperation[], objectName?: string, _attrNames?: string[]): Promise<INewRow> {
        await this.waitForReload();
        const actualObject = await OWManager.newObject(objectName || this._className, extendedOperations, this._getRequestContext());
        const dr = await this.addToObjectList(actualObject);

        dr.actualObject.isChanged = true;
        await this._setRightsOnElement(actualObject);
        dr.row[this.permissionKey] = await this.getRowPermission(actualObject.instanceID);
        this.valueChanged.trigger(this, this.objectList);

        await this.newCreated.trigger(this, dr.row);
        return { actualObject: actualObject, row: dr.row };
    }

    /**
     * Vytvoří nový řádek a oznámí jeho změnu
     * @param attrName Pole stringu obsahujici seznam atributu
     * @param actualObject Actual object pro nacteni hodnot
     */
    public async newRowFromOwoBject(actualObject: OWObject): Promise<INewRow> {
        const row = await this.addToObjectList(actualObject);

        this.valueChanged.trigger(this, this.objectList);

        return row;
    }

    /**
     * Vratí informaci o tom, zda dany objekt na zaklade ID existuje v ObjectListu
     * @param id
     */
    public async hasObjectInObjectList(id: string): Promise<boolean> {
        return this.objectList.getItemByID(id) != null;
    }

    /**
     * Přidá předaný objekt do ObjectListu
     * @param actualObject
     */
    public async addToObjectList(actualObject: OWObject): Promise<INewRow> {
        const data = await this._createRowData(actualObject);
        const iid = actualObject.instanceID;

        this._editedObjects.add(iid);
        this._permisions.set(iid, []);

        if (!this.objectList) {
            this.objectList = new OWObjectList(null, (cancellationToken) => this._getRequestContext(cancellationToken));
        }

        this.objectList.add(actualObject);

        actualObject.attributesChanged.add(async (obj, attributeNames) => {
            const promises = attributeNames.map(attrName => obj.getValue(attrName));
            const attributesMap = new Map<string, any>((await Promise.all(promises)).map((value, index) => [attributeNames[index], value]));
            const isChanged = obj.isChanged;

            this.attributesChanged.trigger(this, {
                key: iid,
                isChanged: isChanged,
                values: attributesMap,
                context: obj,
            });
        });
        return { actualObject: actualObject, row: data };
    }

    public async deleteObjectsByInstanceID(instances: string[]): Promise<void> {
        for (const key of instances) {
            await this.objectList.deleteByInstanceID(key);
            this.deleted.trigger(this, { key });
            this._editedObjects.delete(key);
        }
        this.valueChanged.trigger(this, this.objectList);
    }

    public async deleteObject(dataKey: string): Promise<void> {
        const result = await this.objectList.deleteById(dataKey);
        this._editedObjects.delete(dataKey);
        //this.onDelete.trigger(this, dataKey);
        return result;
    }

    public async deleteObjects(items: string[]): Promise<void> {
        for (const dataKey of items) {
            await this.deleteObject(dataKey);
        }

        //if (items.length > 0)
        this.valueChanged.trigger(this, this.objectList);
    }

    public async addOrRemove(obj: OWObject): Promise<void> {
        if (await this.hasObjectInObjectList(obj.id)) {
            await this.objectList.deleteById(obj.id);
            this.isSelectAll = false;
        } else {
            await this.newRowFromOwoBject(obj);
        }
    }

    public clearObjectList(): void {
        if (this.objectList)
            this.objectList.clear();
    }

    public async copyRow(objId: any, _attrNames?: string[]): Promise<INewRow> {
        const oldObject = await this.getObject(objId);
        const actualObject = await oldObject.createCopy();

        const dr = await this.addToObjectList(actualObject);
        await this._setRightsOnElement(actualObject);
        dr.row[this.permissionKey] = await this.getRowPermission(actualObject.instanceID);
        dr.actualObject.isChanged = true;
        this.valueChanged.trigger(this, this.objectList);

        //this.objectList.Add(actualObject);

        //const data: IRowData = {};
        //const _attrNames = attrNames || this._attributes;
        //const id = actualObject.instanceID;

        //data[this.dataKey] = id;
        //this._editedObjects.push(id);

        //await PromiseUtils.for(0, _attrNames.length, async (index) => {
        //    const attrName = _attrNames[index];
        //    data[attrName] = await actualObject.GetValue(attrName);
        //});

        //actualObject.AttributeChanged.Add(async (obj, prm) => {
        //    const map_val = new Map<string, any>();
        //    map_val.set(prm, await obj.GetValue(prm));

        //    this.onAttributesChanged.trigger(this, {
        //        key: id,
        //        isChanged: true,
        //        values: map_val
        //    });
        //});

        this.newCreated.trigger(this, dr.row);

        return { actualObject: actualObject, row: dr.row };
    }

    /**
     *
     * Zruseni provedenych zmen
     *
     * @param key - ID zaznamu, na kterem rusime zmeny
     * @param _actual - jde o aktualni zaznam
     */
    public async cancelChanges(key: string = "", _actual: boolean = false): Promise<void> {
        const ids: string[] = [];

        if (key != "" && this._editedObjects.size > 1) {
            ids.push(key);
            const obj = await this.objectList.getItemByInstanceID(key);
            if (obj.isNew) {
                await this.objectList.deleteByInstanceID(key);
                this.deleted.trigger(this, { key });
            }
            this._editedObjects.delete(key);
        } else {
            for (const key of this._editedObjects) {
                const obj = await this.objectList.getItemByInstanceID(key);
                if (!obj || !obj.isChanged)
                    continue;
                if (obj.isNew) {
                    await this.objectList.deleteByInstanceID(key);
                    this.deleted.trigger(this, { key });
                    continue;
                }
                ids.push(key);
            }

            this.changesCancelled.trigger(this, null);

            this._editedObjects = new Set();
        }

        this.valueChanged.trigger(this, this.objectList);
    }

    /**
     * Oznámení o změně všech načítaných atributů nově načteného objektu
     * @param obj
     */
    private async _triggerObjectAttributesChanged(obj: OWObject, _originalID?: string): Promise<void> {
        const values = new Map<string, any>();
        for (const attr of this._attributes) {
            try {
                values.set(attr, await obj.getValue(attr));
            } catch (ex) {
                // TODO: chtelo by to mozna pro tuhle vyjimku udelat specialni typ
                if (!(ex.message && /Class [\d\w_]+ doesn't contain attribute/ig.test(ex.message) && attr.includes("|")))
                    throw ex;
            }
        }
        //Pokud mi jiny uzivatel smaze zaznam, na ktery jsem klik tak obj ma ID = -666 a potrebuju vedet puvodni ID abyz oznacil radek.
        //const id = obj.ID == "-666" && originalID != undefined ? originalID : obj.ID;
        this.attributesChanged.trigger(this, {
            key: obj.instanceID,
            isChanged: obj.isChanged || false,
            values: values,
            context: obj,
        });
    }

    public saveObjects(_saveOnlyKeys?: string[]): Promise<SaveResultData[]> {
        throw new Error("Not implemented");
    }
    public setFilterCondition(_condition: string): void {
        throw new NotImplementedException();
    }
    public getFilterCondition(): string {
        throw new NotImplementedException();
    }
    /**
     * Vrací true, pokud je datasource filtrován.
     */
    public get hasFilterCondition(): boolean {
        return false;
    }

    /**
    * Metoda pro dočtení jednoho určitého záznamu
    * @param dataKey Datový klíč (většinou ID) k načtení
    * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public async loadSpecificRecord(dataKey: string, _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData> {
        if (!this._internalRows)
            return null;

        return this._internalRows.find(row => row[this.dataKey] === dataKey);
    }

    /**
    * Metoda pro dočtení určitých záznamů
    * @param dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param _selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    public async loadSpecificRecords(dataKeys: string[], _selectLoaded: boolean = false, _cancellationToken: CancellationToken = CancellationToken.none): Promise<IRowData[]> {
        if (!this._internalRows)
            return [];

        return this._internalRows.filter(row => dataKeys.includes(row[this.dataKey].valueOf()));
    }

    /*
    * Vrací počet zadaných záznamů pro aktuálně nastavený filtr
    */
    public async count(): Promise<IRecordsInfo> {
        return {
            isFiltered: false,
            totalCount: await getDataReaderClient().getCount(this.className, this.condition, this.conditionParams, undefined, this._getRequestContext()),
        };
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._frameContext, cancellationToken);
    }
}
